import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  useGetInvoicePdfQuery,
  useRegenerateInvoicePdfMutation,
} from '../../Service/extendedInvoicesApi';
import { extractApiErrorMessage } from '../../../../Utils/apiErrorUtils';

export interface InvoicePdfPreviewModalProps {
  open: boolean;
  onClose: () => void;
  invoiceId: number | null;
  invoiceNumber?: string;
}

const InvoicePdfPreviewModal: React.FC<InvoicePdfPreviewModalProps> = ({
  open,
  onClose,
  invoiceId,
  invoiceNumber,
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  // Fetch PDF only when modal is open and invoiceId is provided
  const {
    data: pdfBlob,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetInvoicePdfQuery(invoiceId!, {
    skip: !open || !invoiceId,
    refetchOnMountOrArgChange: true,
  });
  const [regenerateInvoicePdf, { isLoading: isMutating, originalArgs }] =
    useRegenerateInvoicePdfMutation();

  const errorMessage = extractApiErrorMessage(error, 'Nieznany błąd');
  // The mutation hook outlives the invoice shown, so only count a run for the current invoice
  const isRegenerating = isMutating && originalArgs?.id === invoiceId;
  const isBusy = isRegenerating || isFetching;
  // Initial load has its own spinner in the content area
  const showRefreshing = isRegenerating || (isFetching && !isLoading);

  const invoiceIdRef = useRef(invoiceId);
  invoiceIdRef.current = invoiceId;

  // The dialog stays mounted while closed, so drop a stale refresh error on open or invoice change
  useEffect(() => {
    setRefreshError(null);
  }, [open, invoiceId]);

  // Create Object URL from Blob (also when modal reopens with cached Blob)
  useEffect(() => {
    if (open && pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);

      // Cleanup: revoke URL when component unmounts or blob changes
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [open, pdfBlob]);

  // Cleanup URL when modal closes
  useEffect(() => {
    if (!open && pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  }, [open, pdfUrl]);

  // Download handler
  const handleDownload = () => {
    if (!pdfBlob) return;

    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Faktura_${invoiceNumber || invoiceId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Backend renders the modern template; the invalidated Invoice tag refetches the PDF query
  const handleRefresh = async () => {
    if (!invoiceId) return;

    setRefreshError(null);
    try {
      await regenerateInvoicePdf({ id: invoiceId }).unwrap();
    } catch (err) {
      // Ignore a late failure of a previous invoice
      if (invoiceIdRef.current === invoiceId) {
        setRefreshError(extractApiErrorMessage(err, 'Nie udało się odświeżyć PDF'));
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>Podgląd faktury {invoiceNumber ? `#${invoiceNumber}` : `ID: ${invoiceId}`}</Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {isLoading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexGrow={1}
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        )}

        {refreshError && (
          <Box p={3} pb={0}>
            <Alert severity="error">{refreshError}</Alert>
          </Box>
        )}

        {isError && (
          <Box p={3}>
            <Alert severity="error">
              Błąd podczas ładowania PDF: {errorMessage}
            </Alert>
          </Box>
        )}

        {pdfUrl && !isLoading && !isError && (
          <iframe
            src={pdfUrl}
            title={`Faktura ${invoiceNumber || invoiceId}`}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              flexGrow: 1,
            }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleRefresh}
          startIcon={showRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          disabled={!invoiceId || isBusy}
        >
          {showRefreshing ? 'Odświeżanie...' : 'Odśwież PDF'}
        </Button>
        <Button
          onClick={handleDownload}
          startIcon={<DownloadIcon />}
          disabled={!pdfBlob || isBusy}
        >
          Pobierz PDF
        </Button>
        <Button onClick={onClose} variant="contained">
          Zamknij
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePdfPreviewModal;
